<script setup>
  import { onMounted, onUnmounted, ref, computed, watch } from "vue"
  import { map, find } from "lodash"
  import { io } from 'socket.io-client'
  import QRCodeVue3 from 'qrcode.vue'

  const props = defineProps({
    sessionId: {
      type: String,
      required: true
    }
  })

  // Flag to track connection status
  const isConnected = ref(false)

  // Latches true the first time a control pad connects; persists for the browser session
  const controlPadEverConnected = ref(sessionStorage.getItem('botc-control-pad-ever-connected') === 'true')

  // Generate a random session ID if none exists
  const localSessionId = ref(props.sessionId || Math.random().toString(36).substring(2, 8).toUpperCase())

  // Watch for changes to props.sessionId
  watch(() => props.sessionId, (newSessionId) => {
    if (newSessionId) {
      localSessionId.value = newSessionId
    }
  })

  defineEmits(['reset'])

  import token from "@/components/token.vue"
  import screen from "@/assets/Blood_on_the_Clocktower_Screen.jpg"

  import characterJSON from '@/assets/characters.json'
  import { cards as cardsData } from '@/assets/cards.js'

  // Resolve the Socket.IO server URL.
  const socketUrl = window.location.origin

  // Create socket but don't connect immediately
  const socket = io( socketUrl, {
    autoConnect: false,
    maxHttpBufferSize: 25 * 1024 * 1024
  });

  // Debug alert to show connection URL
  // alert(`http://${window.location.hostname}:3001`)

  // socket.on( "connect_error", ( err ) => {
  //   let errorMessage = `Error: Connect Error: ${ err.message }`
  //   if ( err.description ) {
  //     errorMessage += `\nDescription: ${ err.description }`
  //   }
  //   if ( err.context ) {
  //     // Be careful with err.context, it can be a complex object.
  //     // For an alert, you might just want its string representation or specific properties.
  //     errorMessage += `\nContext: ${ typeof err.context === 'object' ? JSON.stringify( err.context ) : err.context }`
  //   }
  //   alert( errorMessage )
  //
  //   if ( !socket.active ) {
  //     alert( "Warning: Connection was explicitly denied by the server (e.g., authentication failed)." )
  //   }
  // } )

  const selectedCards = ref([])
  const selectedCharacters = ref( [] )
  const selectedAlignment = ref(null)
  const selectedNumber = ref(null)

  // Custom note state
  const customNote = ref('')
  const showCustomNote = ref(false)

  // Captured image state
  const capturedImage = ref(null)
  const imageRotation = ref(0)

  // Timer state
  const timerActive = ref(false)
  const timerEndTime = ref(0)
  const timerRemaining = ref(0)
  const timerInterval = ref(null)
  const timerExpired = ref(false)
  const timerExpiredTimeout = ref(null)

  // Format time as MM:SS
  function formatTime(seconds) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
  }

  // Update timer display
  function updateTimer() {
    if ( timerEndTime.value > 0 ) {
      const now = Date.now()
      const remaining = ( timerEndTime.value - now ) / 1000 // Convert to seconds

      if ( remaining <= 0 ) {
        // Timer finished but keep it visible for 30 seconds
        if (!timerExpired.value) {
          timerExpired.value = true
          timerRemaining.value = 0
          clearInterval( timerInterval.value )
          timerInterval.value = null

          // Keep timer visible for 30 seconds then hide it
          if (timerExpiredTimeout.value) {
            clearTimeout(timerExpiredTimeout.value)
          }
          timerExpiredTimeout.value = setTimeout(() => {
            timerActive.value = false
            timerExpired.value = false
            timerExpiredTimeout.value = null
          }, 30000) // 30 seconds
        }
      }
      else {
        timerActive.value = true
        timerExpired.value = false
        timerRemaining.value = remaining
      }
    }
  }

  // Computed property for timer display class
  const timerClass = computed(() => {
    return {
      'timer-display': true,
      'timer-normal': timerRemaining.value > 60 && !timerExpired.value, // More than 1 minute
      'timer-warning': timerRemaining.value <= 60 && !timerExpired.value, // Less than 1 minute but not expired
      'timer-expired': timerExpired.value // Timer at 0
    }
  })

  // Computed property for formatted time
  const formattedTime = computed(() => {
    return formatTime(timerRemaining.value)
  })

  // Computed property for control pad URL with session ID
  // Use the current origin so protocol/host/port all match the display page
  const controlPadUrl = computed(() => {
    const baseUrl = window.location.origin
    return `${baseUrl}/control?session=${localSessionId.value}`
  })

  onMounted(() => {
    // Set up connection event handlers
    socket.on('connect', () => {
      isConnected.value = true
      // Join the session room as a display
      console.log( `Joining session: ${localSessionId.value} as display` )
      socket.emit( 'joinSession', localSessionId.value, 'display' )

      // Keep QR code visible until a control pad connects or it's explicitly reset
    })

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error)
      isConnected.value = false
    })

    // Listen for control pad connection status updates
    socket.on('controlPadConnectionStatus', (connected) => {
      if (connected) {
        controlPadEverConnected.value = true
        sessionStorage.setItem('botc-control-pad-ever-connected', 'true')
      }
    })

    socket.on('disconnect', () => {
      isConnected.value = false
    })

    // Connect to the socket after a delay to ensure QR code is shown
    setTimeout(() => {
      socket.connect()
    }, 2000) // 2 second delay

    socket.on( 'cardsUpdated', ( items ) => {
      selectedCards.value = map( items || [], ( item ) => find( cards.value, [ 'name', item ] ) ).filter(Boolean)
    })

    socket.on( 'initialCards', ( items ) => {
      selectedCards.value = map( items || [], ( item ) => find( cards.value, [ 'name', item ] ) ).filter(Boolean)
    })

    // Legacy support
    socket.on( 'cardUpdated', ( item ) => {
      selectedCards.value = item ? [find( cards.value, [ 'name', item ] )].filter(Boolean) : []
    })

    socket.on( 'initialCard', ( item ) => {
      selectedCards.value = item ? [find( cards.value, [ 'name', item ] )].filter(Boolean) : []
    })

    socket.on( 'initialCharacters', ( items ) => {
      selectedCharacters.value = map( items, ( item ) => {
        return find( characterJSON, [ 'id', item ] )
      })
    })

    socket.on( 'charactersUpdated', ( items ) => {
      selectedCharacters.value = map( items, ( item ) => {
        return find( characterJSON, [ 'id', item ] )
      })
    })

    socket.on( 'initialAlignment', ( alignment ) => {
      selectedAlignment.value = alignment
    })

    socket.on( 'alignmentUpdated', ( alignment ) => {
      selectedAlignment.value = alignment
    })

    // Listen for timer updates
    socket.on( 'timerUpdate', ( data ) => {
      timerEndTime.value = data.endTime

      // Clear existing interval if any
      if (timerInterval.value) {
        clearInterval(timerInterval.value)
      }

      // Start a new interval to update the timer every second
      timerInterval.value = setInterval(updateTimer, 1000)

      // Initial update
      updateTimer()
    })

    // Listen for timer reset
    socket.on( 'timerReset', () => {
      timerActive.value = false
      timerEndTime.value = 0
      timerRemaining.value = 0
      timerExpired.value = false

      // Clear interval if any
      if (timerInterval.value) {
        clearInterval(timerInterval.value)
        timerInterval.value = null
      }

      // Clear timeout if any
      if (timerExpiredTimeout.value) {
        clearTimeout(timerExpiredTimeout.value)
        timerExpiredTimeout.value = null
      }
    })

    // Listen for custom note updates
    socket.on( 'customNoteUpdated', ( data ) => {
      customNote.value = data.text
      showCustomNote.value = data.show
    })

    // Listen for QR code reset
    socket.on( 'qrCodeReset', () => {
      controlPadEverConnected.value = false
    })

    // Listen for number selection
    socket.on( 'numberSelected', ( number ) => {
      selectedNumber.value = number
    })

    // Listen for image updates
    socket.on( 'imageUpdated', ( data ) => {
      capturedImage.value = data.image
      imageRotation.value = data.rotation
    })

    // Listen for image rotation updates
    socket.on( 'imageRotationUpdated', ( rotation ) => {
      imageRotation.value = rotation
    })

    // Listen for image reset
    socket.on( 'imageReset', () => {
      capturedImage.value = null
      imageRotation.value = 0
    })

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && socket.disconnected) {
        console.log('Tab active, reconnecting socket...')
        socket.connect()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    onUnmounted(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (timerInterval.value) {
        clearInterval(timerInterval.value)
      }
      if (timerExpiredTimeout.value) {
        clearTimeout(timerExpiredTimeout.value)
      }
    })
  })

  const cards = ref(cardsData)

  const allNotes = computed(() => {
    const list = []
    if (showCustomNote.value && customNote.value) {
      list.push({ id: 'custom', text: customNote.value })
    }
    selectedCards.value.forEach((card, index) => {
      if (card && card.text) {
        list.push({ id: `card-${card.name || index}`, text: card.text })
      }
    })
    return list
  })

  function transformPath( inputPath, type ) {
    const filename = inputPath.substring(inputPath.lastIndexOf('/') + 1);
    return `/tokens/${type}/${filename}`;
  }
</script>

<template lang="pug">
  .row.content.text-center.mt-5
    // Show QR code until a control pad has connected for the first time
    .qr-code-corner( v-if="!controlPadEverConnected" )
      h3.mb-2.text-center Control Pad
      .qr-code-container.mb-2
        QRCodeVue3.mt-3( :value="controlPadUrl" :size="150" level="H" :color="{ dark: '#000000', light: '#ffffff' }" )
      h4.mt-2.text-center
        a( :href="controlPadUrl" target="_blank" ) SessionID: {{ localSessionId }}

    // Show default screen when no content is selected
    .col-12.mt-n5( v-if="selectedCards.length === 0 && selectedCharacters.length === 0 && !showCustomNote && selectedNumber === null && !capturedImage" )
      img.w-100( :src="screen" )

    // Timer display
    .timer-container( v-if="timerActive || timerExpired" )
      .timer( :class="timerClass" )
        span {{ formattedTime }}

    .col-12( v-if="allNotes.length > 0" )
      template( v-for="(note, index) in allNotes" :key="note.id" )
        .alert.alert-light.text-uppercase
          | {{ note.text }}
        .note-separator.my-0( v-if="index < allNotes.length - 1" )
          font-awesome-icon( icon="plus" )

    template( v-if="selectedNumber !== null" )
      .row
        .col-12
          | {{ selectedNumber }}

    template( v-if="capturedImage" )
      // Fullscreen image container to cover the entire viewport while preserving aspect ratio
      .fullscreen-image-container
        img.fullscreen-image( :src="capturedImage" :style="{ transform: `rotate(${imageRotation}deg)`, transformOrigin: 'center center' }" )

    template( v-if="selectedCharacters.length <= 2" )
      .row
        .col-12
          .image-container.mb-3( v-for="character in selectedCharacters" )
            token.mx-auto( :center-image="transformPath( character.image, character.team )"
                   :name="character.name"
                   :id="character.id"
                   size="large"
                   :alignment="selectedAlignment"
                   :showAlignment="true" )

    template( v-else )
      .row.g-0
        .col( v-for="character in selectedCharacters" )
          token.mx-auto( :center-image="transformPath( character.image, character.team )"
                         :name="character.name"
                         :id="character.id"
                         size="large"
                         :alignment="selectedAlignment"
                         :showAlignment="true" )

</template>

<style scoped>
  .content {
    flex-grow: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    font-size: 6rem;
    line-height: 6rem;
  }

  .image-container {
    align-items: center; /* Center image horizontally */
    position: relative; /* Positioning context for overlay */
  }

  .image-container img {
    text-align: center;
    max-width: 100%;
    height: auto;
  }

  .timer-container {
    position: fixed;
    top: 45px;
    z-index: 1000;
  }

  .timer {
    font-size: 10rem; /* Twice as big font size */
    font-weight: bold;
    padding: 38px 25px; /* Twice as tall padding */
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }

  .timer-normal {
    color: #28a745; /* Green */
    background-color: rgba(0, 0, 0, 0.9); /* More opaque */
  }

  .timer-warning {
    color: #dc3545; /* Red */
    background-color: rgba(0, 0, 0, 0.9); /* More opaque */
    animation: pulse 1s infinite;
  }

  .timer-expired {
    color: #ffffff; /* White text */
    background-color: #dc3545; /* Solid red background */
  }

  .timer-expired span {
    display: inline-block;
    animation: text-pulse 1s infinite;
  }

  @keyframes pulse {
    0% {
      background-color: rgba(0, 0, 0, 0.9);
    }
    50% {
      background-color: rgba(255, 255, 0, 0.9); /* Yellow contrasting color */
      color: #000000; /* Black text for better contrast on yellow */
    }
    100% {
      background-color: rgba(0, 0, 0, 0.9);
    }
  }

  @keyframes text-pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
    100% {
      opacity: 1;
    }
  }

  /* QR Code styles */
  .qr-code-container {
    background-color: white;
    border-radius: 10px;
  }

  .qr-code-corner {
    position: fixed;
    top: 12px;
    left: 12px;
    z-index: 1000;
    background-color: rgba(0, 0, 0, 0.7);
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    width: auto; /* Allow natural width */
    margin: 0; /* Reset any margins */
  }

  /* QR code link styles */
  a {
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;
  }

  /* Fullscreen image display */
  .fullscreen-image-container {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    background: #000; /* black background to avoid white borders */
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    z-index: 500; /* below QR/timer (1000), above base content */
  }

  .fullscreen-image {
    /* Ensure the entire image is visible in the viewport while maintaining aspect ratio */
    max-width: 100vw;
    max-height: 100vh;
    width: auto;
    height: auto;
    object-fit: contain; /* show whole image */
    display: block;
    will-change: transform;
    backface-visibility: hidden;
  }

  a:hover {
    color: #0056b3;
    text-decoration: underline;
  }

  .note-separator {
    color: rgba(255, 255, 255, 0.8);
    font-size: 3rem;
  }

</style>
